/**
 * Pure helpers for reading Sync State back out of whatever a device wrote, and
 * for checking what a caller asks to write into it.
 *
 * Shared rather than reimplemented per platform, which is the opposite of what
 * Settings does: each settings store validates its own file because the two
 * clamp platform-specific values. Sync State is pure shape, and one of its
 * fields will later be adopted from a Jot Server — a desktop and a web device
 * that disagreed about which records are readable would disagree about which
 * Note is the Scratchpad.
 *
 * Reads are lenient and writes are strict, and the asymmetry is deliberate. A
 * record already on disk gets coerced because a device that cannot read its Sync
 * State must still open its Scratchpad; a caller handing over an id that is not
 * one has a bug, and coercing that to "unpaired" would silently unpair a paired
 * device and drop it back to recency, which is the divergence ADR-0001 exists to
 * prevent.
 */

import { MAX_SCRATCHPAD_BYTES } from './scratchpad-limits'
import type { ScratchpadRecord } from './sync-wire'
import type { LastAgreed, NoteId, SyncState } from './types'

/**
 * The shape of `crypto.randomUUID()`, the only id Jot mints, and the same check
 * the IPC layer applies before an id is allowed to become a filename. Applied
 * here too because a Scratchpad id is the one piece of Sync State that will
 * arrive over the network: "it was on disk" is not a reason to trust it.
 */
const NOTE_ID = /^[0-9a-fA-F-]{36}$/

/**
 * The Jot Server's own cap on a device label, in code points — the unit both the
 * server and the client count in, so a label ending in an emoji is never judged
 * by its UTF-16 length.
 */
export const MAX_PUSHED_BY_LENGTH = 200

/**
 * The largest instant `Date` can represent, and so the largest `Intl` can format.
 *
 * Exported because the formatter needs the same number: this module decides which
 * timestamps are allowed in, and `relativeTime` refuses to throw on one that got
 * past it some other way. Two predicates, deliberately — one is stricter than the
 * other — but one constant, because a limit stated twice is a limit that drifts.
 */
export const MAX_TIME = 8_640_000_000_000_000

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Epoch milliseconds, held as strictly as a revision is.
 *
 * The strictness is not symmetry for its own sake: this value reaches
 * `Intl.DateTimeFormat.format`, which throws `RangeError` on anything outside the
 * range `Date` can represent, and it is *persisted* — so a value waved through
 * once is read back and thrown on at every launch, which is a good deal worse
 * than one wrong timestamp on screen.
 */
export function isTimestamp(value: unknown): value is number {
  // Zero is 1970 and negative is earlier still: not a time any Jot ever stamped,
  // so a value below it is a broken clock or a hostile answer either way.
  return (
    typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= MAX_TIME
  )
}

/** A device label within the cap, counted by code point. */
function isCappedLabel(value: string): boolean {
  // More than twice the cap in code units cannot be within it in code points, and
  // asking that first keeps a hostile value from being enumerated at all.
  if (value.length > MAX_PUSHED_BY_LENGTH * 2) return false
  return Array.from(value).length <= MAX_PUSHED_BY_LENGTH
}

/**
 * Whether a value could be a Note id at all.
 *
 * Exported because the sync path has to answer it *before* writing: a Jot Server
 * holding an id of some other shape is one this device can never adopt, since
 * the IPC door would refuse it, and finding that out from a rejected `set` would
 * mean the refusal arrives after the decision to pair.
 */
export function isNoteId(value: unknown): value is NoteId {
  return typeof value === 'string' && NOTE_ID.test(value)
}

function asScratchpadId(value: unknown): NoteId | null {
  return isNoteId(value) ? value : null
}

/** A revision the Jot Server issued. It counts from 1, so `0` is not one. */
function isServerRev(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 1
}

/**
 * The Last-Agreed Body: a revision, and the body agreed at it, or nothing.
 *
 * A record holding one without the other is not half an agreement, it is none. The
 * body is what both of the engine's comparisons are made against — "have I changed
 * since then" and, at a `409`, "have they" — and neither can be answered from a
 * revision alone, so the honest reading is that this device has no agreement on
 * record and has to re-establish one from a pairing `GET` (`runOnce`).
 *
 * `scratchpadId` beside it is untouched by that, and the asymmetry is the point:
 * losing the agreement costs a round trip, while losing the pairing drops the device
 * back to recency, which is the divergence ADR-0001 exists to prevent.
 */
function asLastAgreed(value: unknown): LastAgreed | null {
  if (!isRecord(value)) return null
  if (!isServerRev(value.rev)) return null
  // Bounded like a Pending Conflict's body and for the same reason: this record is
  // parsed out of a file before the panel can paint.
  if (typeof value.body !== 'string' || value.body.length > MAX_SCRATCHPAD_BYTES) return null
  return { rev: value.rev, body: value.body }
}

/**
 * Project a stored Pending Conflict back onto the wire record it came from.
 *
 * Field by field, and with `id` held to the same shape as `scratchpadId`: this
 * record arrived from a Jot Server, so "it was on disk" is not a reason to trust
 * it — and an id that is not a Note id could never be adopted anyway, because
 * the IPC door would refuse it.
 *
 * Every field is bounded as well as typed. Two of them leave this module for
 * somewhere unforgiving: `updatedAt` reaches a formatter that throws, and `body`
 * is written back out to a file the next launch parses before it can paint.
 */
function asPendingConflict(value: unknown): ScratchpadRecord | null {
  if (!isRecord(value)) return null

  const id = asScratchpadId(value.id)
  if (id === null) return null
  if (typeof value.body !== 'string' || value.body.length > MAX_SCRATCHPAD_BYTES) return null
  if (!isServerRev(value.rev)) return null
  if (!isTimestamp(value.updatedAt)) return null
  if (typeof value.pushedBy !== 'string' || !isCappedLabel(value.pushedBy)) return null

  return {
    id,
    body: value.body,
    rev: value.rev,
    updatedAt: value.updatedAt,
    pushedBy: value.pushedBy,
  }
}

/**
 * Project arbitrary JSON onto `SyncState`, field by field.
 *
 * Anything missing or nonsensical becomes the unpaired value instead of an
 * error, because a device that cannot read its Sync State is indistinguishable
 * from a device that never had any, and ADR-0001 makes `list[0]` the bootstrap
 * path for exactly that case. A truncated or hand-mangled file therefore costs
 * one boot's identity and never the ability to open the Scratchpad.
 *
 * This doubles as the migration story, and there is nothing else to it: a record
 * written by an older version lacks the keys that appeared since and picks up their
 * unpaired defaults, which for every one of them costs at most a round trip. Nothing
 * about Sync State has shipped in a release, so there is no other shape to carry —
 * the `scratchpadId` is the one field where a default would be a loss, and it has
 * been in this record since the first commit that wrote one.
 */
export function normaliseSyncState(raw: unknown): SyncState {
  const source: Record<string, unknown> = isRecord(raw) ? raw : {}
  return {
    scratchpadId: asScratchpadId(source.scratchpadId),
    // A dropped agreement costs one round trip — the device rediscovers the
    // server's revision with the `GET` it would have made when pairing — where a
    // half-read one would push against a revision nothing ever issued.
    lastAgreed: asLastAgreed(source.lastAgreed),
    // A dropped Pending Conflict is the one loss that matters here: sync starts
    // pushing again, and the winning record is no longer on hand to describe. It
    // costs no text either way, because the Conflict is still on the server and
    // the next push meets the same `409`.
    pendingConflict: asPendingConflict(source.pendingConflict),
  }
}

/**
 * Check a patch on its way *into* Sync State, and reject the whole thing rather
 * than salvage part of it.
 *
 * Every `SyncStateStore.set` goes through this, on both platforms, so `set`
 * means one thing: the desktop store is reached over IPC from the least trusted
 * process and the web store is called straight from the renderer, and a patch
 * one of them refuses must not be quietly persisted by the other. Throwing
 * before anything is written is the other half of that — the record a rejected
 * `set` leaves behind is the one that was already there.
 *
 * Rebuilding the patch from known keys instead of forwarding the caller's object
 * is what keeps a `__proto__` own property — which survives structured cloning,
 * so it does reach the main process — out of the merge below.
 */
export function assertSyncStatePatch(value: unknown): Partial<SyncState> {
  if (!isRecord(value)) throw new Error('Sync state patch must be an object')

  const patch: Partial<SyncState> = {}

  if (value.scratchpadId !== undefined) {
    const id = value.scratchpadId === null ? null : asScratchpadId(value.scratchpadId)
    // An explicit null is legitimate — it is how a device forgets the Scratchpad
    // it was paired to — whereas a value that merely *became* null here is a
    // caller holding something that was never an id.
    if (id === null && value.scratchpadId !== null) {
      throw new Error('Sync state "scratchpadId" must be a note id or null')
    }
    patch.scratchpadId = id
  }

  // Both of the records below are patched whole or not at all, and an explicit
  // null is a value: forgetting the agreement and clearing a resolved Conflict
  // are the two writes the sync flow makes most often.
  if (value.lastAgreed !== undefined) {
    const agreed = value.lastAgreed === null ? null : asLastAgreed(value.lastAgreed)
    if (agreed === null && value.lastAgreed !== null) {
      throw new Error(
        'Sync state "lastAgreed" must be a server revision and the body agreed at it, or null',
      )
    }
    patch.lastAgreed = agreed
  }

  if (value.pendingConflict !== undefined) {
    const conflict =
      value.pendingConflict === null ? null : asPendingConflict(value.pendingConflict)
    if (conflict === null && value.pendingConflict !== null) {
      throw new Error('Sync state "pendingConflict" must be a scratchpad record, or null')
    }
    patch.pendingConflict = conflict
  }

  return patch
}

/**
 * Apply a patch key by key. `undefined` means "leave this alone", while `null`
 * is a value in its own right: a device that unpairs has to be able to forget
 * the Scratchpad id, which `??` would make unrepresentable.
 */
export function applySyncStatePatch(base: SyncState, patch: Partial<SyncState>): SyncState {
  return {
    scratchpadId: patch.scratchpadId !== undefined ? patch.scratchpadId : base.scratchpadId,
    lastAgreed: patch.lastAgreed !== undefined ? patch.lastAgreed : base.lastAgreed,
    pendingConflict:
      patch.pendingConflict !== undefined ? patch.pendingConflict : base.pendingConflict,
  }
}
